import React, { useState } from 'react';
import { Dumbbell, ShoppingCart, Zap } from 'lucide-react';
import { Button } from './ui/button';
import VoucherPurchaseModal from './VoucherPurchaseModal';

const GymVoucherCard = ({ voucher }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID').format(price);
    };

    const handleBuy = (e) => {
        e.stopPropagation();
        setIsModalOpen(true);
    };

    return (
        <>
            <div
                className="relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group cursor-pointer"
                data-testid={`gym-voucher-${voucher.id}`}
                onClick={handleBuy}
            >
                {/* Wide Horizontal Banner Card */}
                <div className="relative bg-gradient-to-r from-[#0a0a1a] via-[#111827] to-[#1a1a2e] flex items-stretch min-h-[160px] md:min-h-[180px] overflow-hidden">

                    {/* Background Pattern - Gym Texture */}
                    <div className="absolute inset-0 opacity-[0.07]"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        }}
                    />

                    {/* Diagonal Accent Strip */}
                    <div className="absolute -right-8 -top-8 w-32 h-32 bg-[#AAFF00]/10 rotate-45 rounded-xl" />
                    <div className="absolute -left-4 -bottom-4 w-24 h-24 bg-[#AAFF00]/5 rotate-12 rounded-full" />

                    {/* Left Section - Icon & Label */}
                    <div className="relative z-10 flex flex-col items-center justify-center px-4 md:px-6 border-r border-white/10 min-w-[80px] md:min-w-[100px]">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#AAFF00] to-[#7ACC00] rounded-xl flex items-center justify-center shadow-lg shadow-[#AAFF00]/20 mb-2">
                            <Dumbbell className="w-6 h-6 md:w-7 md:h-7 text-[#0a0a1a]" />
                        </div>
                        <span className="text-[#AAFF00] text-[9px] md:text-[10px] font-bold uppercase tracking-[0.15em] text-center leading-tight">
                            Gym<br />Voucher
                        </span>
                    </div>

                    {/* Center Section - Value & Price */}
                    <div className="relative z-10 flex-1 flex flex-col justify-center px-4 md:px-6 py-4">
                        {/* Gift Voucher Label */}
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-white/40 text-[10px] md:text-xs font-medium uppercase tracking-[0.2em]">
                                Gift Voucher
                            </span>
                            <div className="h-[1px] flex-1 bg-gradient-to-r from-white/20 to-transparent" />
                        </div>

                        {/* Voucher Value */}
                        <div className="flex items-baseline gap-1 mb-2">
                            <span className="text-white/50 text-sm md:text-base font-medium">Rp</span>
                            <h3 className="text-white text-2xl md:text-3xl font-extrabold tracking-tight leading-none"
                                style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                                {formatPrice(voucher.value)}
                            </h3>
                        </div>

                        {/* Strikethrough Price - Red Promo */}
                        <div className="flex items-center gap-2">
                            <span className="text-red-400 line-through text-xs md:text-sm font-medium decoration-red-500 decoration-2">
                                Rp{formatPrice(voucher.buyPrice)}
                            </span>
                            <span className="bg-red-500/20 text-red-300 text-[9px] md:text-[10px] font-bold px-1.5 py-0.5 rounded">
                                HEMAT Rp{formatPrice(voucher.buyPrice - voucher.value)}
                            </span>
                        </div>
                    </div>

                    {/* Right Section - CTA */}
                    <div className="relative z-10 flex flex-col items-center justify-center px-3 md:px-5 gap-2">
                        {/* Promo Badge */}
                        <div className="bg-[#FF3B30] text-white text-[9px] md:text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-lg flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            Promo
                        </div>

                        {/* Buy Button */}
                        <Button
                            onClick={handleBuy}
                            className="bg-[#AAFF00] hover:bg-[#95E000] text-[#0a0a1a] rounded-xl px-4 md:px-6 py-2.5 text-xs md:text-sm font-extrabold flex items-center gap-1.5 transition-all duration-200 group-hover:scale-105 shadow-lg shadow-[#AAFF00]/20 whitespace-nowrap"
                            data-testid={`buy-voucher-${voucher.id}`}
                        >
                            <ShoppingCart className="w-3.5 h-3.5 md:w-4 md:h-4" />
                            Beli
                        </Button>
                    </div>
                </div>
            </div>

            {/* Purchase Modal */}
            <VoucherPurchaseModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                voucher={voucher}
            />
        </>
    );
};

export default GymVoucherCard;
